import React, { useEffect, useState } from 'react';
import { PaymentsServiceApi } from '../../services/PaymentsServiceApi';
import { UsageEventsTracker } from '../../services/UsageEventsTracker';
import { Header, Footer } from '../../components';

// Variant B: User chooses plan first, then registers, then later verifies email.
// This page lists plans and creates a Stripe Checkout Session for the selected plan.
// Assumes backend endpoint supports planAlias -> price.

function PlanSignupPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [creatingSession, setCreatingSession] = useState(false);
  // navigate, user, accessToken removed - not used in current implementation

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    UsageEventsTracker.recordPageView('/signup/plans');
    PaymentsServiceApi.listPlans()
      .then(list => { if (mounted) setPlans(list); })
      .catch(e => { if (mounted) setError(e.message || 'Failed to load plans'); })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const successUrl = `${window.location.origin}/signup/success`;
  const cancelUrl = `${window.location.origin}/signup/cancel`;

  const handleSelectPlan = async (plan) => {
    if (creatingSession) return;
    setCreatingSession(true);
    setError(null);
    const planAlias = plan.alias || plan.planAlias || plan.id || plan.key;
    UsageEventsTracker.recordUserAction('PLAN_SIGNUP_STARTED', {
      action: 'select',
      category: 'subscription',
      label: 'plan_signup',
      metadata: { planAlias, source: 'plan_signup_page' }
    });
    try {
      // Determine planAlias or fallback priceId
      const session = await PaymentsServiceApi.createCheckoutSession({ planAlias, successUrl, cancelUrl });
      if (session && session.url) {
        window.location.href = session.url;
        return;
      }
      throw new Error('No session URL returned');
    } catch (e) {
      setError(e.message || 'Unable to start checkout');
      setCreatingSession(false);
    }
  };

  return (
    <div className="plan-signup-page">
      <Header />
      <main className="plan-signup-container" style={{ maxWidth: 960, margin: '0 auto', padding: '48px 24px' }}>
        <h1 style={{ fontSize: '2.2rem', marginBottom: '8px' }}>Choose Your Plan</h1>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
          Select a subscription tier. After secure payment you will verify your email and finish account setup.
        </p>
        {loading && <div>Loading plans…</div>}
        {error && <div className="error" style={{ marginBottom: '16px' }}>{error}</div>}
        <div className="plans-grid" style={{ display: 'grid', gap: '24px', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))' }}>
          {plans.map(plan => {
            const alias = plan.alias || plan.planAlias || plan.id || plan.key;
            const label = plan.label || plan.name || alias;
            const amount = plan.amount || plan.unitAmount;
            const currency = (plan.currency || 'usd').toUpperCase();
            const interval = plan.interval || plan.recurringInterval || 'month';
            const price = typeof amount === 'number' ? `${(amount/100).toLocaleString(undefined,{ style:'currency', currency })}/${interval === 'month' ? 'mo' : interval}` : interval;
            return (
              <div key={alias} className="plan-card" style={{
                background: 'var(--semantic-surface-card)',
                border: '1px solid var(--border-subtle)',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.06)'
              }}>
                <h2 style={{ fontSize: '1.25rem', margin: '0 0 8px' }}>{label}</h2>
                <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '12px' }}>{price}</div>
                {plan.description && <p style={{ fontSize: '.85rem', lineHeight: 1.4, minHeight: 48 }}>{plan.description}</p>}
                <button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={creatingSession}
                  className="select-plan-btn"
                  style={{
                    marginTop: '16px',
                    width: '100%',
                    padding: '14px 18px',
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    background: 'var(--color-brand-primary)',
                    color: 'var(--color-text-inverse)',
                    fontWeight: 600,
                    fontSize: '.95rem',
                    transition: 'background .25s'
                  }}
                >{creatingSession ? 'Starting…' : 'Get Started'}</button>
              </div>
            );
          })}
        </div>
        {(!loading && plans.length === 0 && !error) && (
          <div style={{ marginTop: '24px', fontSize: '.9rem', color: 'var(--text-muted)' }}>No plans available right now.</div>
        )}
        <div style={{ marginTop: '48px', fontSize: '.75rem', color: 'var(--text-muted)' }}>
          Secure payments are processed by Stripe. You will verify your email after checkout to activate your subscription.
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default PlanSignupPage;
